import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCatalogStructure } from '../api';
import { describeRecentActivity, loadRecentActivity } from '../utils/recentActivity';

const STEPS = [
  {
    title: 'Pick your grade and skill',
    body: 'Questions are mapped to the standards your school already follows.',
  },
  {
    title: 'Answer in your own words',
    body: 'Type your solution the way you would write it on a test.',
  },
  {
    title: 'Get graded instantly',
    body: 'See what went wrong right away, and watch your accuracy climb skill by skill.',
  },
];

function HomePage({ isAuthenticated, onRequestLogin }) {
  const navigate = useNavigate();
  const pickerRef = useRef(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recent] = useState(loadRecentActivity);
  const [startAfterLogin, setStartAfterLogin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCatalogStructure()
      .then((data) => {
        if (!cancelled) {
          setCatalog(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load catalog');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const states = useMemo(() => catalog?.states || [], [catalog]);
  const singleState = states.length === 1 ? states[0] : null;
  const gradeLevels = singleState?.gradeLevels || [];

  const questionTotal = useMemo(
    () => states.reduce((sum, state) => sum + (state.questionCount || 0), 0),
    [states]
  );

  const scrollToPicker = useCallback(() => {
    pickerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Ask for an account up front, then drop the student at the grade picker.
  const handleStartPracticing = () => {
    if (!isAuthenticated) {
      setStartAfterLogin(true);
      onRequestLogin();
      return;
    }
    scrollToPicker();
  };

  useEffect(() => {
    if (!isAuthenticated || !startAfterLogin) return;
    setStartAfterLogin(false);
    scrollToPicker();
  }, [isAuthenticated, startAfterLogin, scrollToPicker]);

  return (
    <div className="home-page">
      <section className="hero">
        <span className="hero-eyebrow">Built on your state's math standards</span>
        <h1 className="hero-title">
          Practice exactly what your school teaches.
          <span className="hero-title-accent"> Get graded instantly.</span>
        </h1>
        <p className="hero-subtitle">
          Pick a grade, choose a skill, and answer real standards-aligned questions.
          Every answer is graded on the spot with feedback you can learn from.
        </p>

        <div className="hero-actions">
          <button type="button" className="hero-cta-primary" onClick={handleStartPracticing}>
            Start Practicing
          </button>
          {recent ? (
            <button
              type="button"
              className="hero-cta-secondary"
              onClick={() => navigate(recent.path)}
            >
              Continue Learning
              <span className="hero-cta-note">{describeRecentActivity(recent)}</span>
            </button>
          ) : (
            <a className="hero-cta-secondary" href="#how-it-works">
              See how it works
            </a>
          )}
        </div>

        <ul className="hero-highlights">
          <li>
            <strong>{questionTotal ? questionTotal.toLocaleString() : '4,500'}+</strong>
            standards-aligned questions
          </li>
          <li>
            <strong>Instant</strong>
            grading with written feedback
          </li>
          <li>
            <strong>Skill by skill</strong>
            progress you can actually see
          </li>
        </ul>
      </section>

      <section className="home-picker" ref={pickerRef}>
        <div className="home-section-header">
          <h2 className="home-section-title">
            {singleState ? 'Choose your grade' : 'Choose your state'}
          </h2>
          <p className="home-section-subtitle">
            {singleState
              ? `${singleState.name} · your learning path starts here`
              : 'We follow the standards taught in your state.'}
          </p>
        </div>

        {loading && (
          <div className="browse-loading">
            <div className="browse-loading-spinner" />
            <span>Loading grades…</span>
          </div>
        )}

        {error && <div className="error">{error}</div>}

        {!loading && !error && states.length === 0 && (
          <div className="browse-empty">No practice content is available yet.</div>
        )}

        {!loading && !error && singleState && (
          <div className="grade-level-cards-grid">
            {gradeLevels.map((gl) => {
              const pathCount = (gl.majorTopics || []).length || (gl.topics || []).length;
              return (
                <button
                  key={gl.name}
                  type="button"
                  className="grade-level-card"
                  onClick={() => navigate(
                    `/state/${encodeURIComponent(singleState.name)}/grade/${encodeURIComponent(gl.name)}`
                  )}
                >
                  <span className="grade-level-card-name">{gl.name}</span>
                  <span className="grade-level-card-count">
                    {pathCount} learning path{pathCount !== 1 ? 's' : ''}
                  </span>
                  <span className="grade-level-card-arrow">→</span>
                </button>
              );
            })}
          </div>
        )}

        {!loading && !error && !singleState && states.length > 0 && (
          <div className="grade-level-cards-grid">
            {states.map((state) => {
              const gradeCount = state.gradeCount || (state.gradeLevels || []).length;
              return (
                <button
                  key={state.name}
                  type="button"
                  className="grade-level-card"
                  onClick={() => navigate(`/state/${encodeURIComponent(state.name)}`)}
                >
                  <span className="grade-level-card-name">{state.name}</span>
                  <span className="grade-level-card-count">
                    {gradeCount} grade{gradeCount !== 1 ? 's' : ''}
                  </span>
                  <span className="grade-level-card-arrow">→</span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="home-steps" id="how-it-works">
        <div className="home-section-header">
          <h2 className="home-section-title">How it works</h2>
        </div>
        <div className="home-steps-grid">
          {STEPS.map((step, index) => (
            <div key={step.title} className="home-step-card">
              <span className="home-step-number">{index + 1}</span>
              <h3 className="home-step-title">{step.title}</h3>
              <p className="home-step-body">{step.body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default HomePage;
